import * as React from 'react';
import styled from 'styled-components';
import { pipe, tuple } from 'fp-ts/lib/function';
import * as A from 'fp-ts/lib/Array';
import * as O from 'fp-ts/lib/Option';
import { useIORef, useRefable, useUpdateEffect } from '../../core/hooks';
import * as Input from '../../core/Input';
import { base } from '../Input';
import { splitClasses } from '../utils';
import { useSetter, useGetter, useEffect } from './slots';
import { inputInnerStyle, ctxSelectClass } from './shared';
import { filterText, isData } from './utils';
import { Data, Value } from './types';
import { BOTTOM_CURSOR } from './Cursor';

declare module './slots' {
    interface SelectControls {
        'Input': 'Input',
    }
}


interface ControlInput {
    value: Record<string, any> | null,
    className?: string,
    placeholder?: string
}

const emptyValue = base.empty;

const isValueForInput = (x: any): x is { text: string } => 
    pipe(
        x,
        x => tuple(typeof(x) === 'object' && x !== null, x),
        ([s, x]) => s && typeof(x.text) === 'string',
    );

const takeText = (value: Value<{ text: string }> | null) => 
    pipe(
        value,
        x => isValueForInput(x) ? O.some(x) : O.none,
        O.fold(
            () => emptyValue,
            (s) => s.text,
        ),
    );

/**
 * Функция нахождения текста для инпута по стартовому значению
 * @param data 
 * @param value 
 */
export const getStartText = (data: Data<{ text: string }>, value: Value<{ text: string }> | null) =>
    pipe(
        O.fromNullable(value),
        O.chain(x => A.findFirst<Value<{ text: string }>>(d => d.text === x.text)(data)),
        O.fold(
            () => emptyValue,
            (s) => s.text,
        ),
    );

export const takeCursorData = (value: Record<string, any> | null, cursor: number, data: Record<string, any>[]) =>
    pipe(
        data,
        A.lookup(cursor),
        O.fold(
            () => value || {},
            (s) => s,
        ),
    );

const Component = React.forwardRef<HTMLInputElement, ControlInput>(({ placeholder = '', value, className }, ref) => {
    
    const inputRef = useRefable(ref);

    const derivedValue = React.useMemo(() => pipe(
        value,
        x => isValueForInput(x) ? O.some(x) : O.none,
        O.fold(
            () => ({ text: '' }),
            (s) => s,
        ),
    ), [value]);

    const {
        read: getStartValue,
        write: writeStartValue,
    } = useIORef(derivedValue);
    
    const {
        read: getValue,
        write: writeValue,
        modify: modifyValue,
    } = useIORef(derivedValue);

    const [changeValueControl, run] =   useEffect('changeValue');
    const [, changeTargetRun] =         useEffect('changeTarget');
    const [cursorMove] =                useEffect('cursorMove');
    const [selectInactivate] =          useEffect('selectInactivate');

    const dropdownTarget =              useGetter('dropdownTarget');
    const select =                      useGetter('select');
    const cursor =                      useGetter('cursor');

    const _data = dropdownTarget.getData();

    const data = isData(_data) ? _data : [];

    const startText = React.useMemo(() => getStartText(data, derivedValue), [data, derivedValue]);

    const inputControl = Input.useControl(base, startText);
    const dirtyInputControl = Input.useDirtyControl(inputRef);

    const onChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const text = e.target.value;

        inputControl.inoutput(text);

        dropdownTarget.changeData(filterText(data, text));
        
        modifyValue(x => ({
            ...x,
            text,
        }))();
    }, [data]);


    React.useEffect(() => {
        const value = getValue();

        inputControl.inoutput(takeText(value));
    }, [changeValueControl]);

    const localChange = (value: typeof derivedValue) => {
        writeValue(value)();
        inputControl.inoutput(value.text);
    }

    /**
     * API
     */
    const change = run((value) => {
        if(isValueForInput(value)){
            localChange(value);
        }
        else {
            console.warn(`
                Тип значения ${JSON.stringify(value)} не поддерживается инпутом. 
                Должен быть объект с полем 'text'!
            `);
        }
    });

    const reset = React.useCallback(run(() => {
        localChange({ text: base.empty });
    }), []);

    const isEmpty = React.useCallback(() => {
        return inputControl.isEmpty();
    }, []);

    const active = React.useCallback(() => {
        dirtyInputControl.focus();
    }, []);

    const inactive = React.useCallback(() => {
        dirtyInputControl.blur();
    }, []);

    useSetter('target', {
        tag: 'Input',
        isEmpty,
        getValue,
        getStartValue,
        change,
        reset,
        active,
        inactive,
    });


    useUpdateEffect(() => {
        const data = dropdownTarget.getMutableData();
        const cursorPosition = cursor.get();

        const value = takeCursorData(getValue(), cursorPosition, data);

        localChange(value as typeof derivedValue);

        if(cursorPosition === BOTTOM_CURSOR && select.isActive()){
            dirtyInputControl.focus();
        }
    }, [cursorMove]);
    

    React.useEffect(() => {
        change(derivedValue);
        writeStartValue(derivedValue)();
    }, [derivedValue]);

    React.useEffect(() => {
        inactive();
    }, [selectInactivate]);

    return (
        <InputS 
            className={splitClasses(ctxSelectClass('input'), className)}
            ref={inputRef}
            value={inputControl.displayValue}
            onChange={changeTargetRun(onChange)}
            placeholder={placeholder}
        />
    );
});

export default Component;

const InputS = styled.input`
    ${inputInnerStyle}
`;